import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Calendar, User, ArrowRight } from "lucide-react";
import { SEOHead, breadcrumbSchema, articleSchema } from "../components/SEOHead";
import Breadcrumbs from "../components/Breadcrumbs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/blog/${slug}`),
      axios.get(`${API}/blog`),
    ]).then(([postRes, allRes]) => {
      setPost(postRes.data);
      setRelated(allRes.data.filter(p => p.slug !== slug).slice(0, 3));
    }).catch(() => {}).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <main className="pt-16 min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-black border-t-[#FFD600] rounded-full animate-spin mx-auto mb-4" />
        <p className="font-bold">Loading...</p>
      </div>
    </main>
  );

  if (!post) return (
    <main className="pt-16 min-h-screen flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-4xl font-black mb-4">Post Not Found</h1>
        <Link to="/blog" className="btn-primary">Back to Blog</Link>
      </div>
    </main>
  );

  return (
    <main className="pt-16" data-testid="blog-post-page">
      <SEOHead
        title={post.title}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        image={post.image_url}
        schema={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Blog", path: "/blog" },
            { name: post.title, path: `/blog/${post.slug}` },
          ]),
          articleSchema(post.title, post.excerpt, `/blog/${post.slug}`, post.author, post.created_at),
        ]}
      />
      <Breadcrumbs items={[
        { name: "Blog", path: "/blog" },
        { name: post.title, path: `/blog/${post.slug}` },
      ]} />

      {post.image_url && (
        <div className="w-full h-64 md:h-80 border-b-4 border-black overflow-hidden">
          <img src={post.image_url} alt={post.title} className="w-full h-full object-cover" />
        </div>
      )}

      <div className="container-main max-w-3xl py-16">
        <Link to="/blog" className="inline-flex items-center gap-2 font-bold text-sm mb-8 hover:text-[#0055FF] transition-colors" data-testid="back-to-blog">
          <ArrowLeft size={14} /> Back to Blog
        </Link>

        <span className="text-xs font-black uppercase tracking-widest text-[#0055FF] bg-blue-50 border border-[#0055FF] px-3 py-1 rounded-full">
          {post.category}
        </span>

        <h1 className="text-3xl md:text-5xl font-black mt-4 mb-6 leading-tight" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="post-title">
          {post.title}
        </h1>

        <div className="flex items-center gap-6 text-sm text-gray-500 mb-10 pb-8 border-b-2 border-black">
          <span className="flex items-center gap-1.5 font-semibold">
            <User size={14} /> {post.author}
          </span>
          <span className="flex items-center gap-1.5">
            <Calendar size={14} />
            {new Date(post.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" })}
          </span>
        </div>

        <div
          className="blog-content text-gray-800 leading-relaxed"
          dangerouslySetInnerHTML={{ __html: post.content }}
          data-testid="post-content"
        />

        {/* CTA */}
        <div className="mt-16 bg-[#FFD600] border-2 border-black rounded-2xl p-8 text-center" style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}>
          <h3 className="text-2xl font-black mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
            Ready to Get Your Website?
          </h3>
          <p className="font-semibold mb-6">Professional websites from $499. Live in 48 hours. 100% Aussie support.</p>
          <Link to="/contact" className="btn-secondary inline-flex items-center gap-2" data-testid="post-cta">
            Get a Free Quote <ArrowRight size={16} />
          </Link>
        </div>
      </div>

      {/* Related posts */}
      {related.length > 0 && (
        <section className="section-padding bg-[#F4F4F5] border-t-4 border-black">
          <div className="container-main">
            <h2 className="text-3xl font-black mb-8" style={{ fontFamily: "Outfit, sans-serif" }}>More Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((p, i) => (
                <Link key={p.id} to={`/blog/${p.slug}`} className="group card-brutal" data-testid={`related-post-${i}`}>
                  <span className="text-xs font-black uppercase tracking-widest text-[#0055FF] mb-2 block">{p.category}</span>
                  <h3 className="font-black text-lg mb-2 group-hover:text-[#0055FF] transition-colors" style={{ fontFamily: "Outfit, sans-serif" }}>{p.title}</h3>
                  <p className="text-sm text-gray-600">{p.excerpt}</p>
                  <div className="flex items-center gap-1 mt-4 text-sm font-bold text-[#0055FF]">Read More <ArrowRight size={12} /></div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
